import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Users,
  Brain,
  Trophy,
  ArrowRight,
  CheckCircle,
  Briefcase,
  GraduationCap,
  FileText,
  Target,
  BarChart,
  UserCheck,
  ClipboardList,
  Award,
  Zap,
  TrendingUp,
  Video,
  Search,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import BookDemoModal from "../components/BookDemoModal";

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState("jobseeker");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const jobSeekerServices = [
    {
      icon: Brain,
      title: "Intelligent Skill Assessments",
      description: "Take intelligent tests that adapt to your skill level and provide instant feedback on your performance."
    },
    {
      icon: FileText,
      title: "Resume Parsing & Optimization",
      description: "Upload your resume and get intelligent insights to optimize it for better job matches."
    },
    {
      icon: Award,
      title: "Skills Verification & Badges",
      description: "Earn verified badges after completing assessments to showcase your skills to potential employers."
    },
    {
      icon: Video,
      title: "Intelligent Interview Practice",
      description: "Practice with automated interviews that simulate real hiring scenarios and improve your interview skills."
    },
    {
      icon: GraduationCap,
      title: "Personalized Learning Path",
      description: "Get customized course recommendations based on your test results and career goals."
    },
    {
      icon: TrendingUp,
      title: "Application Tracking",
      description: "Track all your job applications and test submissions in one centralized dashboard."
    },
    {
      icon: Target,
      title: "Skill Gap Analysis",
      description: "Identify areas for improvement with detailed analytics on your strengths and weaknesses."
    },
    {
      icon: Zap,
      title: "Instant Results & Feedback",
      description: "Receive immediate test results with comprehensive feedback to help you improve continuously."
    }
  ];

  const hrServices = [
    {
      icon: Briefcase,
      title: "Automated Test Creation",
      description: "Upload job descriptions and automatically generate customized technical tests tailored to your requirements."
    },
    {
      icon: Search,
      title: "Automated Candidate Screening",
      description: "Screen hundreds of candidates efficiently with intelligent evaluation and ranking systems."
    },
    {
      icon: Shield,
      title: "Advanced Proctoring",
      description: "Monitor tests with camera tracking, tab switching detection, and comprehensive violation reporting."
    },
    {
      icon: UserCheck,
      title: "Candidate Management",
      description: "Manage your candidate pipeline with easy shortlist, reject, and save features for better organization."
    },
    {
      icon: BarChart,
      title: "Detailed Analytics Dashboard",
      description: "Access comprehensive analytics on candidate performance, test completion rates, and hiring metrics."
    },
    {
      icon: ClipboardList,
      title: "Job Posting Management",
      description: "Create and manage multiple job postings, each with custom tests and evaluation criteria."
    },
    {
      icon: Users,
      title: "Bulk Candidate Evaluation",
      description: "Evaluate large batches of candidates simultaneously with automated scoring and comparison tools."
    },
    {
      icon: Trophy,
      title: "Skills-Based Matching",
      description: "Match candidates to positions using intelligent skill analysis and compatibility scoring."
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "Intelligent Testing",
      description:
        "Generate adaptive tests automatically from job descriptions using advanced automation",
    },
    {
      icon: Users,
      title: "Complete Hiring Solution",
      description:
        "End-to-end platform for HR teams, recruiters, and job seekers",
    },
    {
      icon: Trophy,
      title: "Skill Verification",
      description:
        "Verified badges and certifications to showcase candidate abilities",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description:
        "Bank-level security and privacy protection for all your data",
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100"
          : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-0 -ml-1 group transition-all duration-300"
            >
              <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />
              <span className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
                VetriaAI
              </span>
            </Link>

            <div className="flex items-center">
              <Link
                to="/login"
                className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Modern Hero Section */}
      <section className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-6 sm:mb-8 animate-fade-in">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">Intelligent Recruitment Platform</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Revolutionize Your Hiring with
            <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent">
              Intelligent Automation
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
            Transform your recruitment process with intelligent testing,
            automated screening, and data-driven insights. Perfect for HR teams,
            recruiters, and job seekers.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4">
            <Link
              to="/login"
              className="group w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="group w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center"
            >
              Book Your Demo
              <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm text-gray-500 px-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success flex-shrink-0" />
              <span>Intelligent Technology</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success flex-shrink-0" />
              <span>Secure & Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-success flex-shrink-0" />
              <span>Trusted by Early Adopters</span>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Features Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Core Features</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Everything You Need to Hire Smarter
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Our comprehensive platform combines intelligent automation with human
              expertise to deliver exceptional hiring outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modern Metrics Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Our Impact</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Our Vision & Goals
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              As a growing startup, we're committed to transforming hiring. Here's what we're building towards:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="group text-center p-6 sm:p-8 bg-gradient-to-br from-white to-gray-50 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-red-700 bg-clip-text text-transparent mb-2 sm:mb-3">
                10,000+
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium px-2">
                Target: Candidates to be screened in Year 1
              </div>
            </div>
            <div className="group text-center p-6 sm:p-8 bg-gradient-to-br from-white to-gray-50 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-red-700 bg-clip-text text-transparent mb-2 sm:mb-3">
                500+
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium px-2">
                Target: Companies to support by 2026
              </div>
            </div>
            <div className="group text-center p-6 sm:p-8 bg-gradient-to-br from-white to-gray-50 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-red-700 bg-clip-text text-transparent mb-2 sm:mb-3">
                80%
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium px-2">
                Average time reduction in pilot tests
              </div>
            </div>
            <div className="group text-center p-6 sm:p-8 bg-gradient-to-br from-white to-gray-50 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-red-700 bg-clip-text text-transparent mb-2 sm:mb-3">
                95%+
              </div>
              <div className="text-sm sm:text-base text-gray-600 font-medium px-2">
                Accuracy in skill-based shortlisting
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Services Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Our Services</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Tailored Solutions for Everyone
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
              Discover what we offer for both job seekers and HR professionals
            </p>

            {/* Modern Toggle Button */}
            <div className="inline-flex items-center bg-white rounded-full p-1 sm:p-1.5 shadow-lg border border-gray-200 w-full sm:w-auto max-w-md mx-auto">
              <button
                onClick={() => setSelectedUserType("hr")}
                className={`flex-1 sm:flex-none px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm md:text-base transition-all duration-300 ${selectedUserType === "hr"
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                For HRs / Founders
              </button>
              <button
                onClick={() => setSelectedUserType("jobseeker")}
                className={`flex-1 sm:flex-none px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm md:text-base transition-all duration-300 ${selectedUserType === "jobseeker"
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                For Job Seekers
              </button>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {(selectedUserType === "jobseeker" ? jobSeekerServices : hrServices).map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-5 sm:p-6 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/login"
              className="group inline-flex items-center bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Modern Featured By Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Recognition</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Featured By
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4">
              Recognized by leading platforms and ecosystems
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {/* FindMyAI */}
            <a
              href="https://fyndmyai.com/tools/vetriaai"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2 flex flex-col items-center justify-center text-center"
            >
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mb-3 sm:mb-4 flex items-center justify-center bg-gray-50 rounded-card group-hover:bg-primary/5 transition-colors duration-300">
                <img
                  src="/findMyAi.jpg"
                  alt="FindMyAI Logo"
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                FindMyAI
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Featured on FindMyAI Platform
              </p>
            </a>

            {/* Telangana Startup */}
            <a
              href="https://www.tg10x.com/companies/vetriaai"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2 flex flex-col items-center justify-center text-center"
            >
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mb-3 sm:mb-4 flex items-center justify-center bg-gray-50 rounded-card group-hover:bg-primary/5 transition-colors duration-300">
                <img
                  src="/Telangana_startup.jpg"
                  alt="Telangana Startup Logo"
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                Telangana Startup Ecosystem
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Part of Telangana's Innovation Hub
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Modern Call to Action */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
            <span className="text-xs sm:text-sm font-semibold">Get Started Today</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">
            Ready to transform your hiring?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 opacity-95 max-w-2xl mx-auto px-4">
            Join early adopters using VetriaAI to hire smarter, faster, and more efficiently.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Book Your Demo
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Modern Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link
                to="/"
                className="mb-4 group transition-all duration-300 inline-flex items-center gap-0 -ml-1"
              >
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent group-hover:from-red-400 group-hover:via-primary group-hover:to-red-400 transition-all duration-300">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/cancellations-refunds" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>

      <BookDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
